/* eslint-disable typebox-snake-case/typebox-object-keys-snake-case -- camelCase keys match existing DB/API schema */
import { Static, Type } from '@sinclair/typebox';
import { PageTypeEnum } from '../translation_namespace/page_type_namespace';
import { WebsiteStylesSchema, WebsiteVerticalSpacingSchema } from './Styles';
import { GOOGLE_TRACKING_IDS_MAX } from '../tracking/site-tracking-constants';

// Formats d'ID de suivi : définis dans un module sans dépendance (lu par le générateur de doc).
export {
  GOOGLE_ADS_CONVERSION_LABEL_REGEX,
  GOOGLE_TRACKING_ID_REGEX,
  GOOGLE_TRACKING_IDS_MAX,
  META_PIXEL_ID_REGEX,
  TIKTOK_PIXEL_ID_REGEX,
} from '../tracking/site-tracking-constants';

export const COMPONENT_IDS = {
  HEADER_HERO: 'header-hero',
  HEADER_MENU: 'header-menu',
  TOP_HEADER: 'top-header',
  FOOTER: 'footer',
  NEWSLETTER_SUBSCRIPTION: 'newsletter-subscription',
  CALL_TO_ACTION: 'call-to-action',
  FAQ: 'faq',
  PRESTATION_LISTING: 'prestation-listing',
  PRESTATION_DETAIL: 'prestation-detail',
  NEWS_LISTING: 'news-listing',
  NEWS_DETAIL: 'news-detail',
  NEWS_HIGHLIGHT: 'news-highlight',
  TEXT_BLOCK: 'text-block',
  TEAM_LISTING: 'team-listing',
  CONTACT_INFO: 'contact-info',
  PRESTATION_HIGHLIGHT: 'prestation-highlight',
  BOOKING_WIDGET: 'booking-widget',
  TESTIMONIALS: 'testimonials',
  KEY_FIGURES: 'key-figures',
  METHODOLOGY: 'methodology',
  PHOTO_GALLERY: 'photo-gallery',
  SIMPLE_PHOTO: 'simple-photo',
  BEFORE_AFTER: 'before-after',
  SPECIALTIES: 'specialties',
  PROMOTION: 'promotion',
  QUOTE: 'quote',
  PARTNER_LOGOS: 'partner-logos',
  CERTIFICATIONS: 'certifications',
  VOD_PLAYER: 'vod-player',
  COMPARISON_TABLE: 'comparison-table',
  COOKIE_POLICY_DETAILS: 'cookie-policy-details',
  INSTAGRAM_FEED: 'instagram-feed',
  AREA_LIST: 'area-list',
  SUB_PAGES_LIST: 'sub-pages-list',
  GOOGLE_REVIEWS: 'google-reviews',
  FLOATING_CONTACT: 'floating-contact',
  CONTACT_FORM: 'contact-form',
  CALLBACK_REQUEST: 'callback-request',
  PACK_LISTING: 'pack-listing',
  GIFT_CARD: 'gift-card',
} as const;

export type ComponentId = (typeof COMPONENT_IDS)[keyof typeof COMPONENT_IDS];

export const COMPONENT_LABEL_KEYS: Record<ComponentId, string> = {
  [COMPONENT_IDS.HEADER_HERO]: 'WEBSITE_BUILDER_COMPONENT_HEADER_HERO',
  [COMPONENT_IDS.HEADER_MENU]: 'WEBSITE_BUILDER_COMPONENT_HEADER_MENU',
  [COMPONENT_IDS.TOP_HEADER]: 'WEBSITE_BUILDER_COMPONENT_TOP_HEADER',
  [COMPONENT_IDS.FOOTER]: 'WEBSITE_BUILDER_COMPONENT_FOOTER',
  [COMPONENT_IDS.CALL_TO_ACTION]: 'WEBSITE_BUILDER_COMPONENT_CALL_TO_ACTION',
  [COMPONENT_IDS.FAQ]: 'WEBSITE_BUILDER_COMPONENT_FAQ',
  [COMPONENT_IDS.NEWSLETTER_SUBSCRIPTION]: 'WEBSITE_BUILDER_COMPONENT_NEWSLETTER_SUBSCRIPTION',
  [COMPONENT_IDS.PRESTATION_LISTING]: 'WEBSITE_BUILDER_COMPONENT_PRESTATION_LISTING',
  [COMPONENT_IDS.PRESTATION_DETAIL]: 'WEBSITE_BUILDER_COMPONENT_PRESTATION_DETAIL',
  [COMPONENT_IDS.NEWS_LISTING]: 'WEBSITE_BUILDER_COMPONENT_NEWS_LISTING',
  [COMPONENT_IDS.NEWS_DETAIL]: 'WEBSITE_BUILDER_COMPONENT_NEWS_DETAIL',
  [COMPONENT_IDS.NEWS_HIGHLIGHT]: 'WEBSITE_BUILDER_COMPONENT_NEWS_HIGHLIGHT',
  [COMPONENT_IDS.TEXT_BLOCK]: 'WEBSITE_BUILDER_COMPONENT_TEXT_BLOCK',
  [COMPONENT_IDS.TEAM_LISTING]: 'WEBSITE_BUILDER_COMPONENT_TEAM_LISTING',
  [COMPONENT_IDS.CONTACT_INFO]: 'WEBSITE_BUILDER_COMPONENT_CONTACT_INFO',
  [COMPONENT_IDS.PRESTATION_HIGHLIGHT]: 'WEBSITE_BUILDER_COMPONENT_PRESTATION_HIGHLIGHT',
  [COMPONENT_IDS.BOOKING_WIDGET]: 'WEBSITE_BUILDER_COMPONENT_BOOKING_WIDGET',
  [COMPONENT_IDS.TESTIMONIALS]: 'WEBSITE_BUILDER_COMPONENT_TESTIMONIALS',
  [COMPONENT_IDS.KEY_FIGURES]: 'WEBSITE_BUILDER_COMPONENT_KEY_FIGURES',
  [COMPONENT_IDS.METHODOLOGY]: 'WEBSITE_BUILDER_COMPONENT_METHODOLOGY',
  [COMPONENT_IDS.PHOTO_GALLERY]: 'WEBSITE_BUILDER_COMPONENT_PHOTO_GALLERY',
  [COMPONENT_IDS.SIMPLE_PHOTO]: 'WEBSITE_BUILDER_COMPONENT_SIMPLE_PHOTO',
  [COMPONENT_IDS.BEFORE_AFTER]: 'WEBSITE_BUILDER_COMPONENT_BEFORE_AFTER',
  [COMPONENT_IDS.SPECIALTIES]: 'WEBSITE_BUILDER_COMPONENT_SPECIALTIES',
  [COMPONENT_IDS.PROMOTION]: 'WEBSITE_BUILDER_COMPONENT_PROMOTION',
  [COMPONENT_IDS.QUOTE]: 'WEBSITE_BUILDER_COMPONENT_QUOTE',
  [COMPONENT_IDS.PARTNER_LOGOS]: 'WEBSITE_BUILDER_COMPONENT_PARTNER_LOGOS',
  [COMPONENT_IDS.CERTIFICATIONS]: 'WEBSITE_BUILDER_COMPONENT_CERTIFICATIONS',
  [COMPONENT_IDS.VOD_PLAYER]: 'WEBSITE_BUILDER_COMPONENT_VOD_PLAYER',
  [COMPONENT_IDS.COMPARISON_TABLE]: 'WEBSITE_BUILDER_COMPONENT_COMPARISON_TABLE',
  [COMPONENT_IDS.COOKIE_POLICY_DETAILS]: 'WEBSITE_BUILDER_COMPONENT_COOKIE_POLICY_DETAILS',
  [COMPONENT_IDS.INSTAGRAM_FEED]: 'WEBSITE_BUILDER_COMPONENT_INSTAGRAM_FEED',
  [COMPONENT_IDS.AREA_LIST]: 'WEBSITE_BUILDER_COMPONENT_AREA_LIST',
  [COMPONENT_IDS.SUB_PAGES_LIST]: 'WEBSITE_BUILDER_COMPONENT_SUB_PAGES_LIST',
  [COMPONENT_IDS.GOOGLE_REVIEWS]: 'WEBSITE_BUILDER_COMPONENT_GOOGLE_REVIEWS',
  [COMPONENT_IDS.FLOATING_CONTACT]: 'WEBSITE_BUILDER_COMPONENT_FLOATING_CONTACT',
  [COMPONENT_IDS.CONTACT_FORM]: 'WEBSITE_BUILDER_COMPONENT_CONTACT_FORM',
  [COMPONENT_IDS.CALLBACK_REQUEST]: 'WEBSITE_BUILDER_COMPONENT_CALLBACK_REQUEST',
  [COMPONENT_IDS.PACK_LISTING]: 'WEBSITE_BUILDER_COMPONENT_PACK_LISTING',
  [COMPONENT_IDS.GIFT_CARD]: 'WEBSITE_BUILDER_COMPONENT_GIFT_CARD',
};

export type ComponentCategory = 'hero' | 'services' | 'marketing' | 'social-proof' | 'content' | 'media' | 'listing';

export const COMPONENT_CATEGORY_LABEL_KEYS: Record<ComponentCategory, string> = {
  hero: 'WEBSITE_BUILDER_CATEGORY_HERO',
  services: 'WEBSITE_BUILDER_CATEGORY_SERVICES',
  marketing: 'WEBSITE_BUILDER_CATEGORY_MARKETING',
  'social-proof': 'WEBSITE_BUILDER_CATEGORY_SOCIAL_PROOF',
  content: 'WEBSITE_BUILDER_CATEGORY_CONTENT',
  media: 'WEBSITE_BUILDER_CATEGORY_MEDIA',
  listing: 'WEBSITE_BUILDER_CATEGORY_LISTING',
};

export const COMPONENT_CATEGORIES: Partial<Record<ComponentId, ComponentCategory>> = {
  [COMPONENT_IDS.HEADER_HERO]: 'hero',
  [COMPONENT_IDS.PRESTATION_HIGHLIGHT]: 'services',
  [COMPONENT_IDS.PACK_LISTING]: 'services',
  [COMPONENT_IDS.GIFT_CARD]: 'services',
  [COMPONENT_IDS.BOOKING_WIDGET]: 'services',
  [COMPONENT_IDS.SPECIALTIES]: 'services',
  [COMPONENT_IDS.CALL_TO_ACTION]: 'marketing',
  [COMPONENT_IDS.NEWSLETTER_SUBSCRIPTION]: 'marketing',
  [COMPONENT_IDS.PROMOTION]: 'marketing',
  [COMPONENT_IDS.TESTIMONIALS]: 'social-proof',
  [COMPONENT_IDS.KEY_FIGURES]: 'social-proof',
  [COMPONENT_IDS.FAQ]: 'content',
  [COMPONENT_IDS.TEXT_BLOCK]: 'content',
  [COMPONENT_IDS.METHODOLOGY]: 'content',
  [COMPONENT_IDS.PHOTO_GALLERY]: 'media',
  [COMPONENT_IDS.SIMPLE_PHOTO]: 'media',
  [COMPONENT_IDS.BEFORE_AFTER]: 'media',
  [COMPONENT_IDS.PRESTATION_LISTING]: 'listing',
  [COMPONENT_IDS.NEWS_LISTING]: 'listing',
  [COMPONENT_IDS.NEWS_HIGHLIGHT]: 'content',
  [COMPONENT_IDS.TEAM_LISTING]: 'listing',
  [COMPONENT_IDS.CONTACT_INFO]: 'listing',
  [COMPONENT_IDS.QUOTE]: 'content',
  [COMPONENT_IDS.PARTNER_LOGOS]: 'social-proof',
  [COMPONENT_IDS.CERTIFICATIONS]: 'social-proof',
  [COMPONENT_IDS.VOD_PLAYER]: 'media',
  [COMPONENT_IDS.COMPARISON_TABLE]: 'content',
  [COMPONENT_IDS.COOKIE_POLICY_DETAILS]: 'content',
  [COMPONENT_IDS.INSTAGRAM_FEED]: 'social-proof',
  [COMPONENT_IDS.TOP_HEADER]: 'marketing',
  [COMPONENT_IDS.AREA_LIST]: 'listing',
  [COMPONENT_IDS.SUB_PAGES_LIST]: 'listing',
  [COMPONENT_IDS.GOOGLE_REVIEWS]: 'social-proof',
  [COMPONENT_IDS.FLOATING_CONTACT]: 'marketing',
  [COMPONENT_IDS.CONTACT_FORM]: 'marketing',
  [COMPONENT_IDS.CALLBACK_REQUEST]: 'marketing',
};

export const COMPONENT_DESCRIPTION_KEYS: Partial<Record<ComponentId, string>> = {
  [COMPONENT_IDS.HEADER_HERO]: 'WEBSITE_BUILDER_COMPONENT_DESC_HEADER_HERO',
  [COMPONENT_IDS.PRESTATION_HIGHLIGHT]: 'WEBSITE_BUILDER_COMPONENT_DESC_PRESTATION_HIGHLIGHT',
  [COMPONENT_IDS.BOOKING_WIDGET]: 'WEBSITE_BUILDER_COMPONENT_DESC_BOOKING_WIDGET',
  [COMPONENT_IDS.TEXT_BLOCK]: 'WEBSITE_BUILDER_COMPONENT_DESC_TEXT_BLOCK',
  [COMPONENT_IDS.NEWSLETTER_SUBSCRIPTION]: 'WEBSITE_BUILDER_COMPONENT_DESC_NEWSLETTER_SUBSCRIPTION',
  [COMPONENT_IDS.CALL_TO_ACTION]: 'WEBSITE_BUILDER_COMPONENT_DESC_CALL_TO_ACTION',
  [COMPONENT_IDS.FAQ]: 'WEBSITE_BUILDER_COMPONENT_DESC_FAQ',
  [COMPONENT_IDS.TESTIMONIALS]: 'WEBSITE_BUILDER_COMPONENT_DESC_TESTIMONIALS',
  [COMPONENT_IDS.KEY_FIGURES]: 'WEBSITE_BUILDER_COMPONENT_DESC_KEY_FIGURES',
  [COMPONENT_IDS.METHODOLOGY]: 'WEBSITE_BUILDER_COMPONENT_DESC_METHODOLOGY',
  [COMPONENT_IDS.PHOTO_GALLERY]: 'WEBSITE_BUILDER_COMPONENT_DESC_PHOTO_GALLERY',
  [COMPONENT_IDS.SIMPLE_PHOTO]: 'WEBSITE_BUILDER_COMPONENT_DESC_SIMPLE_PHOTO',
  [COMPONENT_IDS.BEFORE_AFTER]: 'WEBSITE_BUILDER_COMPONENT_DESC_BEFORE_AFTER',
  [COMPONENT_IDS.SPECIALTIES]: 'WEBSITE_BUILDER_COMPONENT_DESC_SPECIALTIES',
  [COMPONENT_IDS.PROMOTION]: 'WEBSITE_BUILDER_COMPONENT_DESC_PROMOTION',
  [COMPONENT_IDS.PRESTATION_LISTING]: 'WEBSITE_BUILDER_COMPONENT_DESC_PRESTATION_LISTING',
  [COMPONENT_IDS.NEWS_LISTING]: 'WEBSITE_BUILDER_COMPONENT_DESC_NEWS_LISTING',
  [COMPONENT_IDS.NEWS_HIGHLIGHT]: 'WEBSITE_BUILDER_COMPONENT_DESC_NEWS_HIGHLIGHT',
  [COMPONENT_IDS.TEAM_LISTING]: 'WEBSITE_BUILDER_COMPONENT_DESC_TEAM_LISTING',
  [COMPONENT_IDS.CONTACT_INFO]: 'WEBSITE_BUILDER_COMPONENT_DESC_CONTACT_INFO',
  [COMPONENT_IDS.QUOTE]: 'WEBSITE_BUILDER_COMPONENT_DESC_QUOTE',
  [COMPONENT_IDS.PARTNER_LOGOS]: 'WEBSITE_BUILDER_COMPONENT_DESC_PARTNER_LOGOS',
  [COMPONENT_IDS.CERTIFICATIONS]: 'WEBSITE_BUILDER_COMPONENT_DESC_CERTIFICATIONS',
  [COMPONENT_IDS.COMPARISON_TABLE]: 'WEBSITE_BUILDER_COMPONENT_DESC_COMPARISON_TABLE',
  [COMPONENT_IDS.COOKIE_POLICY_DETAILS]: 'WEBSITE_BUILDER_COMPONENT_DESC_COOKIE_POLICY_DETAILS',
  [COMPONENT_IDS.INSTAGRAM_FEED]: 'WEBSITE_BUILDER_COMPONENT_DESC_INSTAGRAM_FEED',
  [COMPONENT_IDS.TOP_HEADER]: 'WEBSITE_BUILDER_COMPONENT_DESC_TOP_HEADER',
  [COMPONENT_IDS.GOOGLE_REVIEWS]: 'WEBSITE_BUILDER_COMPONENT_DESC_GOOGLE_REVIEWS',
  [COMPONENT_IDS.FLOATING_CONTACT]: 'WEBSITE_BUILDER_COMPONENT_DESC_FLOATING_CONTACT',
  [COMPONENT_IDS.SUB_PAGES_LIST]: 'WEBSITE_BUILDER_COMPONENT_DESC_SUB_PAGES_LIST',
  [COMPONENT_IDS.CONTACT_FORM]: 'WEBSITE_BUILDER_COMPONENT_DESC_CONTACT_FORM',
  [COMPONENT_IDS.CALLBACK_REQUEST]: 'WEBSITE_BUILDER_COMPONENT_DESC_CALLBACK_REQUEST',
  [COMPONENT_IDS.GIFT_CARD]: 'WEBSITE_BUILDER_COMPONENT_DESC_GIFT_CARD',
};

export type RenderMode = 'public' | 'builder';

export type PageType = `${PageTypeEnum}`;

export type PageConfigEntry = {
  allowedComponents: ComponentId[];
  defaultComponents?: ComponentId[];
  allowMultiple: boolean;
  defaultPath: string;
  defaultName: string;
  canDisable: boolean;
  defaultEnabled: boolean;
  directDetail?: boolean;
};

export const ComponentInstanceSchema = Type.Object({
  id: Type.String(),
  componentId: Type.String(),
  variant: Type.String(),
  values: Type.Record(Type.String(), Type.Unknown()),
  // Override d'espacement vertical par composant (latent — pas encore exposé dans le builder).
  // Si défini, prime sur le réglage de thème pour ce bloc uniquement (voir WebsiteRenderer mode public).
  spacing: Type.Optional(WebsiteVerticalSpacingSchema),
});
export type ComponentInstance = Static<typeof ComponentInstanceSchema>;

export const ComponentRefSchema = Type.Object({
  id: Type.String(),
  ref: Type.String(),
});
export type ComponentRef = Static<typeof ComponentRefSchema>;

// Permissive schema for AJV validation (avoids removeAdditional stripping with anyOf).
// Semantic validation (ref XOR componentId+variant+values) is handled by validateComponentInstances.
export const PageComponentSchema = Type.Object({
  id: Type.String(),
  ref: Type.Optional(Type.String()),
  componentId: Type.Optional(Type.String()),
  variant: Type.Optional(Type.String()),
  values: Type.Optional(Type.Record(Type.String(), Type.Unknown())),
});
export type PageComponent = ComponentInstance | ComponentRef;

export function isComponentRef(c: PageComponent): c is ComponentRef {
  return 'ref' in c && !('componentId' in c);
}

export const SHARED_COMPONENT_IDS: ReadonlySet<string> = new Set([COMPONENT_IDS.HEADER_MENU, COMPONENT_IDS.FOOTER]);

export const SHARED_PAGE_ID = '__shared__';

/**
 * Page-id composite pour l'édition du contenu d'une zone (`areaOverrides[areaKey]`)
 * dans le builder. Le WebsiteRenderer rend les blocs d'une page-ville sous cet id
 * synthétique ; les handlers de mutation du builder le parsent pour router les
 * changements vers `page.areaOverrides[areaKey].components` au lieu de `page.components`.
 */
export const AREA_PAGE_ID_SEP = '::area::';

export function buildAreaPageId(placesPageId: string, areaKey: string): string {
  return `${placesPageId}${AREA_PAGE_ID_SEP}${areaKey}`;
}

export function parseAreaPageId(pageId: string): { placesPageId: string; areaKey: string } | null {
  const idx = pageId.indexOf(AREA_PAGE_ID_SEP);
  if (idx === -1) return null;
  return { placesPageId: pageId.slice(0, idx), areaKey: pageId.slice(idx + AREA_PAGE_ID_SEP.length) };
}

/**
 * Niveau d'une route virtuelle de la page `services` portant un override par-instance :
 * - `category`   : page catégorie `/prestations/{cat}` (`match.type === 'category'`)
 * - `prestation` : page détail presta `/prestations/{cat}/{slug}` (`match.type === 'detail'`)
 */
export type VirtualOverrideKind = 'category' | 'prestation';

/**
 * Page-id composite pour l'édition du contenu d'une vue virtuelle (catégorie ou détail
 * presta) dans le builder — jumeau de `buildAreaPageId`. Le WebsiteRenderer rend les blocs
 * matérialisés (hérités + propres) sous cet id synthétique ; les handlers de mutation du
 * builder le parsent pour router les changements vers
 * `page.{category|prestation}Overrides[targetId].layout` au lieu de `page.components`.
 * Le `kind` est encodé dans le séparateur pour rester non-ambigu vis-à-vis d'`area`.
 */
export const VIRTUAL_PAGE_ID_SEP_CATEGORY = '::vcat::';
export const VIRTUAL_PAGE_ID_SEP_PRESTATION = '::vpresta::';

export function buildVirtualPageId(servicesPageId: string, kind: VirtualOverrideKind, targetId: string): string {
  const sep = kind === 'category' ? VIRTUAL_PAGE_ID_SEP_CATEGORY : VIRTUAL_PAGE_ID_SEP_PRESTATION;
  return `${servicesPageId}${sep}${targetId}`;
}

export function parseVirtualPageId(
  pageId: string,
): { servicesPageId: string; kind: VirtualOverrideKind; targetId: string } | null {
  const catIdx = pageId.indexOf(VIRTUAL_PAGE_ID_SEP_CATEGORY);
  if (catIdx !== -1) {
    return {
      servicesPageId: pageId.slice(0, catIdx),
      kind: 'category',
      targetId: pageId.slice(catIdx + VIRTUAL_PAGE_ID_SEP_CATEGORY.length),
    };
  }
  const prestaIdx = pageId.indexOf(VIRTUAL_PAGE_ID_SEP_PRESTATION);
  if (prestaIdx !== -1) {
    return {
      servicesPageId: pageId.slice(0, prestaIdx),
      kind: 'prestation',
      targetId: pageId.slice(prestaIdx + VIRTUAL_PAGE_ID_SEP_PRESTATION.length),
    };
  }
  return null;
}

/**
 * Id de bloc synthétique du « composant » Titre & SEO d'une page (ou vue virtuelle).
 * `targetId` = id de page réelle (→ `page.seo`) OU id composite `buildVirtualPageId(...)`
 * (→ `override.seo`). Le suffixe le distingue d'un vrai bloc dans le builder ; le clic
 * ouvre l'inspecteur de droite comme n'importe quel composant.
 */
export const PAGE_SEO_BLOCK_SUFFIX = '__page_seo';
export function buildPageSeoBlockId(targetId: string): string {
  return `${targetId}${PAGE_SEO_BLOCK_SUFFIX}`;
}

// WebsitePageInstance type is defined manually to avoid circular reference
// with the recursive `children` field in the schema.
export interface DetailTemplate {
  variant: string;
  values: Record<string, unknown>;
}

/**
 * Override de contenu pour une zone (city/department/region) sur la page mère
 * de type `places`. Permet d'avoir une page fille `/lieux/[url_slug]` éditée
 * manuellement avec des blocs custom. Si absent (ou `components.length === 0`),
 * la page fille renvoie 404 (anti-thin-content) — cf. plan website-locations.
 */
export interface AreaPageOverride {
  components: PageComponent[];
  title?: string;
  metaDescription?: string;
  updatedAt: string;
}

export const AreaPageOverrideSchema = Type.Object({
  components: Type.Array(PageComponentSchema),
  title: Type.Optional(Type.String()),
  metaDescription: Type.Optional(Type.String()),
  updatedAt: Type.String(),
});

/**
 * Surcharge SEO d'une page (ou d'une vue virtuelle presta/catégorie). Tous les champs
 * sont optionnels : vides → on retombe sur les fallbacks actuels (hint composant →
 * siteTitle/siteDescription → globals pro).
 * - `heading`         : texte du H1 *visible* (override du titre injecté par le renderer).
 *                       Inerte quand le H1 est porté par un hero ou un composant détail.
 * - `metaTitle`       : balise `<title>` (priorité sur tous les fallbacks ; utilisé tel quel).
 * - `metaDescription` : `<meta name="description">` + description OG/Twitter.
 */
/**
 * Niveau de personnalisation SEO de la page (= variante du « composant » Titre & SEO) :
 * - `auto`  : aucun override, SEO 100 % automatique (fallbacks hero/site/pro).
 * - `title` : on surcharge seulement le H1 visible (`heading`).
 * - `full`  : on surcharge H1 + balise `<title>` + meta description.
 * Le mode *gate* l'application : en `auto`, les valeurs stockées sont ignorées.
 */
export type PageSeoMode = 'auto' | 'title' | 'full';

export interface PageSeoOverride {
  mode?: PageSeoMode;
  heading?: string;
  metaTitle?: string;
  metaDescription?: string;
  updatedAt?: string;
}

export const PageSeoOverrideSchema = Type.Object({
  mode: Type.Optional(Type.Union([Type.Literal('auto'), Type.Literal('title'), Type.Literal('full')])),
  heading: Type.Optional(Type.String()),
  metaTitle: Type.Optional(Type.String()),
  metaDescription: Type.Optional(Type.String()),
  updatedAt: Type.Optional(Type.String()),
});

/**
 * Mode effectif d'un override : explicite si présent, sinon inféré des champs remplis
 * (migration douce des données saisies avant l'ajout des variantes — et UX : un champ
 * rempli sans mode reste appliqué).
 */
export function inferPageSeoMode(seo?: PageSeoOverride): PageSeoMode {
  if (!seo) return 'auto';
  if (seo.mode) return seo.mode;
  if (seo.metaTitle || seo.metaDescription) return 'full';
  if (seo.heading) return 'title';
  return 'auto';
}

/**
 * Override *effectif* après application du mode : ce que le rendu et `generateMetadata`
 * doivent réellement consommer. En `auto` → rien (fallbacks). Source unique partagée
 * entre le renderer (H1 public) et custom-websites (balises meta).
 */
export function resolveEffectivePageSeo(seo?: PageSeoOverride): {
  heading?: string;
  metaTitle?: string;
  metaDescription?: string;
} {
  const mode = inferPageSeoMode(seo);
  if (!seo || mode === 'auto') return {};
  if (mode === 'title') return { heading: seo.heading };
  return { heading: seo.heading, metaTitle: seo.metaTitle, metaDescription: seo.metaDescription };
}

/**
 * Schéma *titré* du formulaire « Titre & SEO de la page » (rendu par DynamicForm dans la
 * modale du builder). Distinct de `PageSeoOverrideSchema` (stockage) : porte les clés i18n
 * de label/placeholder. `heading` = override du H1 ; metaTitle/metaDescription = balises SEO.
 */
export const PageSeoFormSchema = Type.Object({
  heading: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_PAGE_SEO_HEADING_LABEL',
      placeholder: 'WEBSITE_BUILDER_PAGE_SEO_HEADING_PLACEHOLDER',
    }),
  ),
  metaTitle: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_PAGE_SEO_META_TITLE_LABEL',
      placeholder: 'WEBSITE_BUILDER_PAGE_SEO_META_TITLE_PLACEHOLDER',
      maxLength: 70,
    }),
  ),
  metaDescription: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_PAGE_SEO_META_DESC_LABEL',
      placeholder: 'WEBSITE_BUILDER_PAGE_SEO_META_DESC_PLACEHOLDER',
      maxLength: 180,
      meta: { multiline: true },
    }),
  ),
});

/**
 * Entrée du layout d'une vue virtuelle (catégorie OU détail presta) personnalisée :
 * - `template` : référence à un bloc hérité du gabarit (page.components / detailTemplate),
 *                résolu EN LIVE au rendu → l'édition du gabarit se propage. Lecture seule.
 * - `extra`    : bloc propre à cette cible (instance complète : éditable / déplaçable /
 *                supprimable).
 * L'ordre du tableau = l'ordre d'affichage : un bloc propre peut donc se placer n'importe
 * où entre des blocs hérités (« position libre » via swap d'index, sans nouveau DnD).
 */
export type VirtualLayoutEntry =
  | { kind: 'template'; templateBlockId: string }
  | { kind: 'extra'; component: ComponentInstance };

// Permissive schema for AJV validation (avoids removeAdditional stripping with anyOf) :
// un Type.Union d'objets fait supprimer `component` par la branche `template` évaluée en
// premier (removeAdditional: 'all'), puis les DEUX branches échouent. Même pattern que
// PageComponentSchema ; la cohérence kind ↔ champ est vérifiée par validateComponentInstances.
export const VirtualLayoutEntrySchema = Type.Object({
  kind: Type.Union([Type.Literal('template'), Type.Literal('extra')]),
  templateBlockId: Type.Optional(Type.String()),
  component: Type.Optional(ComponentInstanceSchema),
});

/**
 * Override de contenu pour UNE prestation ou UNE catégorie, sur la page mère `services`.
 * Permet d'ajouter des blocs propres (ex. « chiffres clés » sur *Coupe homme*) tout en
 * gardant les blocs du gabarit partagés. Même forme pour les deux niveaux ; le store
 * (`prestationOverrides` vs `categoryOverrides`) et la liste de blocs hérités diffèrent.
 */
export interface VirtualPageOverride {
  layout: VirtualLayoutEntry[];
  /** Surcharge SEO propre à cette prestation/catégorie (metaTitle/metaDescription). */
  seo?: PageSeoOverride;
  updatedAt: string;
}

export const VirtualPageOverrideSchema = Type.Object({
  layout: Type.Array(VirtualLayoutEntrySchema),
  seo: Type.Optional(PageSeoOverrideSchema),
  updatedAt: Type.String(),
});

/**
 * Au-delà de ce nombre de sous-pages visibles, le header n'affiche PAS de
 * sous-menu déroulant pour la page parent (un portfolio migré peut porter des
 * dizaines de sous-pages) : seul le lien parent est rendu, la page liste ses
 * enfants dans son contenu (bloc SUB_PAGES_LIST).
 */
export const MAX_MENU_DROPDOWN_CHILDREN = 8;

export interface WebsitePageInstance {
  id: string;
  type: string;
  slug: string;
  title: string;
  showInMenu?: boolean;
  menuOrder?: number;
  components: PageComponent[];
  children?: WebsitePageInstance[];
  detailTemplate?: DetailTemplate;
  /**
   * Présent uniquement quand `type === 'external-link'` : entrée de menu pointant
   * vers une URL externe (ouverte dans un nouvel onglet). Une telle « page » n'a ni
   * contenu ni chemin public — elle est exclue du routing SSR et du sitemap
   * (cf. `flattenPagesWithPaths`) et ne sert qu'à afficher un lien dans le menu.
   */
  externalUrl?: string;
  /**
   * Présent uniquement quand `type === 'places'` (page Lieux du builder).
   * Clé = `area_key` de la zone (cf. service-area.schema.ts). Valeur = contenu
   * manuel rédigé par le pro pour cette zone.
   */
  areaOverrides?: Record<string, AreaPageOverride>;
  /**
   * Présent uniquement quand `type === 'services'` (page Prestations du builder).
   * Blocs propres ajoutés sur la page **détail** d'une prestation donnée.
   * Clé = ID de la prestation (stable au renommage de slug / au changement de catégorie).
   */
  prestationOverrides?: Record<string, VirtualPageOverride>;
  /**
   * Présent uniquement quand `type === 'services'`. Blocs propres ajoutés sur la page
   * **catégorie** `/prestations/{cat}`. Clé = ID de la catégorie. Indépendant de
   * `prestationOverrides` (aucune cascade catégorie → prestas en v1).
   */
  categoryOverrides?: Record<string, VirtualPageOverride>;
  /**
   * Surcharge SEO de la page : H1 visible (`heading`) + balises `<title>` / `<meta description>`.
   * Édité via le panneau « Titre & SEO » du builder (clic sur le H1 ou menu de la page).
   * Tous les champs vides → fallbacks actuels (cf. `PageSeoOverride`).
   */
  seo?: PageSeoOverride;
}

export const WebsitePageInstanceSchema = Type.Object({
  id: Type.String(),
  type: Type.String(),
  slug: Type.String(),
  title: Type.String(),
  showInMenu: Type.Optional(Type.Boolean()),
  menuOrder: Type.Optional(Type.Number()),
  components: Type.Array(PageComponentSchema),
  children: Type.Optional(Type.Array(Type.Unsafe<WebsitePageInstance>({ type: 'object' }))),
  externalUrl: Type.Optional(Type.String()),
  detailTemplate: Type.Optional(
    Type.Object({
      variant: Type.String(),
      values: Type.Record(Type.String(), Type.Unknown()),
    }),
  ),
  areaOverrides: Type.Optional(Type.Record(Type.String(), AreaPageOverrideSchema)),
  prestationOverrides: Type.Optional(Type.Record(Type.String(), VirtualPageOverrideSchema)),
  categoryOverrides: Type.Optional(Type.Record(Type.String(), VirtualPageOverrideSchema)),
  seo: Type.Optional(PageSeoOverrideSchema),
});

export const TrackingConfigSchema = Type.Object({
  // Historique : un seul ID. Lu en repli quand `googleTrackingIds` est absent (aucune migration
  // de données, `tracking_config` est un JSON) — toujours passer par `resolveGoogleTrackingIds`.
  googleTrackingId: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_SETTINGS_GOOGLE_LABEL',
      placeholder: 'WEBSITE_BUILDER_SETTINGS_GOOGLE_PLACEHOLDER',
    }),
  ),
  googleTrackingIds: Type.Optional(Type.Array(Type.String(), { maxItems: GOOGLE_TRACKING_IDS_MAX })),
  // Libellé de conversion Google Ads par événement de site (clé = nom de `SITE_EVENTS`), envoyé
  // en `send_to: 'AW-…/libellé'`. Sans libellé, Google Ads n'enregistre aucune conversion.
  googleAdsConversionLabels: Type.Optional(Type.Record(Type.String(), Type.String())),
  metaPixelId: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_SETTINGS_META_PIXEL_LABEL',
      placeholder: 'WEBSITE_BUILDER_SETTINGS_META_PIXEL_PLACEHOLDER',
    }),
  ),
  tiktokPixelId: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_SETTINGS_TIKTOK_PIXEL_LABEL',
      placeholder: 'WEBSITE_BUILDER_SETTINGS_TIKTOK_PIXEL_PLACEHOLDER',
    }),
  ),
});
export type TrackingConfig = Static<typeof TrackingConfigSchema>;
// Champs à saisie simple de la modale « Suivi » (rendus par DynamicForm) ; les ID Google et les
// libellés de conversion ont leur propre éditeur de liste.
export const TrackingPixelIdsSchema = Type.Pick(TrackingConfigSchema, ['metaPixelId', 'tiktokPixelId']);

export const FaviconCropSchema = Type.Object({
  x: Type.Number(),
  y: Type.Number(),
  width: Type.Number(),
  height: Type.Number(),
});

export const FAVICON_SIZE_KEYS = ['16', '32', '48', '180', '192', '512'] as const;
export type FaviconSizeKey = (typeof FAVICON_SIZE_KEYS)[number];

export const FaviconSizesSchema = Type.Object({
  '16': Type.String(),
  '32': Type.String(),
  '48': Type.String(),
  '180': Type.String(),
  '192': Type.String(),
  '512': Type.String(),
});

export const FaviconSchema = Type.Object({
  url: Type.String(),
  crop: Type.Optional(FaviconCropSchema),
  sizes: Type.Optional(FaviconSizesSchema),
});
export type Favicon = Static<typeof FaviconSchema>;
export type FaviconSizes = Static<typeof FaviconSizesSchema>;

export const SiteConfigSchema = Type.Object({
  favicon: Type.Optional(FaviconSchema),
  siteTitle: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_SETTINGS_TITLE_LABEL',
      placeholder: 'WEBSITE_BUILDER_SETTINGS_TITLE_PLACEHOLDER',
    }),
  ),
  siteDescription: Type.Optional(
    Type.String({
      title: 'WEBSITE_BUILDER_SETTINGS_DESC_LABEL',
      placeholder: 'WEBSITE_BUILDER_SETTINGS_DESC_PLACEHOLDER',
    }),
  ),
});
export type SiteConfig = Static<typeof SiteConfigSchema>;

// ─── Redirections (old → new path, or path → external URL / short link) ─
export const RedirectRuleKind = {
  REDIRECT: 'redirect',
  SHORT_LINK: 'short_link',
} as const;
export type RedirectRuleKindType = (typeof RedirectRuleKind)[keyof typeof RedirectRuleKind];

export const RedirectRuleSchema = Type.Object({
  id: Type.String(),
  // Source path on the website, always starting with "/" (e.g. "/ancienne-presta", "/insta").
  source: Type.String({
    title: 'WEBSITE_SETTINGS_REDIRECTS_SOURCE_LABEL',
    placeholder: 'WEBSITE_SETTINGS_REDIRECTS_SOURCE_PLACEHOLDER',
  }),
  // Destination: either an internal binding key wrapped as "{{prestation:45}}"
  // or a raw external/absolute URL (e.g. "https://instagram.com/...").
  destination: Type.String({
    title: 'WEBSITE_SETTINGS_REDIRECTS_DESTINATION_LABEL',
    placeholder: 'WEBSITE_SETTINGS_REDIRECTS_DESTINATION_PLACEHOLDER',
  }),
  kind: Type.Union([Type.Literal(RedirectRuleKind.REDIRECT), Type.Literal(RedirectRuleKind.SHORT_LINK)], {
    default: RedirectRuleKind.REDIRECT,
  }),
  // true → 308 (permanent, SEO-preserving) ; false → 307 (temporary).
  permanent: Type.Boolean({ default: true }),
  enabled: Type.Boolean({ default: true }),
});
export type RedirectRule = Static<typeof RedirectRuleSchema>;

export const RedirectsConfigSchema = Type.Object({
  rules: Type.Array(RedirectRuleSchema),
});
export type RedirectsConfig = Static<typeof RedirectsConfigSchema>;

// Cap raisonnable sur le nombre de règles de redirection par site.
export const MAX_REDIRECT_RULES = 500;

/**
 * Normalise un chemin source de redirection : garantit un slash initial, retire
 * le slash final (sauf racine), trim. Partagé backend (validation + matching SSR)
 * et frontend (UI). Ne touche pas la casse (les chemins peuvent être sensibles).
 */
export function normalizeRedirectSource(raw: string): string {
  let path = (raw ?? '').trim();
  if (!path) return '';
  if (!path.startsWith('/')) path = '/' + path;
  // Retire le query/hash éventuel : on matche sur le pathname seul.
  path = path.split('?')[0].split('#')[0];
  if (path.length > 1 && path.endsWith('/')) path = path.replace(/\/+$/, '');
  return path;
}

// ─── Scripts custom (plan MAX, colonne `scripts_config` hors draft/publish) ─
// Les scripts n'entrent PAS dans `WebsiteConfig` — modèle `redirects` : colonne
// dédiée + champ top-level `custom_scripts` dans la réponse publique. Conséquences
// voulues : pas de strip dans saveWebsiteConfig, pas d'overlay decorateConfigForClient,
// et les scripts ne s'exécutent JAMAIS dans la preview builder.
export const ScriptEntryKind = {
  EXTERNAL: 'external',
  INLINE: 'inline',
} as const;
export type ScriptEntryKindType = (typeof ScriptEntryKind)[keyof typeof ScriptEntryKind];

export const ScriptPosition = {
  HEAD: 'head',
  BODY_END: 'body_end',
} as const;
export type ScriptPositionType = (typeof ScriptPosition)[keyof typeof ScriptPosition];

export const ScriptConsentCategory = {
  NECESSARY: 'necessary',
  ANALYTICS: 'analytics',
  MARKETING: 'marketing',
} as const;
export type ScriptConsentCategoryType = (typeof ScriptConsentCategory)[keyof typeof ScriptConsentCategory];

export const ScriptEntrySchema = Type.Object({
  id: Type.String(),
  // Nom lisible pour le pro (ex. "Google Analytics", "Widget avis"). Trim non vide, ≤ 100.
  label: Type.String(),
  kind: Type.Union([Type.Literal(ScriptEntryKind.EXTERNAL), Type.Literal(ScriptEntryKind.INLINE)]),
  // kind=external : requis, https uniquement (validé serveur).
  src: Type.Optional(Type.String()),
  // kind=inline : requis, JS brut SANS balises <script> (wrapper unique strippé, validé serveur).
  snippet: Type.Optional(Type.String()),
  position: Type.Union([Type.Literal(ScriptPosition.HEAD), Type.Literal(ScriptPosition.BODY_END)], {
    default: ScriptPosition.HEAD,
  }),
  // Catégorie RGPD : `necessary` rend le script sans consentement ; `analytics`/`marketing`
  // attendent le consentement de la catégorie (CookieConsentBanner côté custom-websites).
  consentCategory: Type.Union(
    [
      Type.Literal(ScriptConsentCategory.NECESSARY),
      Type.Literal(ScriptConsentCategory.ANALYTICS),
      Type.Literal(ScriptConsentCategory.MARKETING),
    ],
    { default: ScriptConsentCategory.ANALYTICS },
  ),
  // external uniquement.
  async: Type.Optional(Type.Boolean()),
  defer: Type.Optional(Type.Boolean()),
  enabled: Type.Boolean({ default: true }),
});
export type ScriptEntry = Static<typeof ScriptEntrySchema>;

export const ScriptsConfigSchema = Type.Object({
  scripts: Type.Array(ScriptEntrySchema),
});
export type ScriptsConfig = Static<typeof ScriptsConfigSchema>;

export const MAX_CUSTOM_SCRIPTS = 20;
export const MAX_SCRIPT_SNIPPET_LENGTH = 10_000; // ~10 KB
export const MAX_SCRIPT_LABEL_LENGTH = 100;

/**
 * Normalise un snippet inline : strippe UN wrapper `<script>…</script>` collé
 * depuis une doc vendor (sans attribut src), trim. Partagé backend (validation)
 * et frontend (modale) pour que les deux validations ne divergent jamais.
 * Un `</script>` résiduel après normalisation reste invalide (casserait la
 * balise au rendu) — rejeté côté serveur.
 */
export function normalizeInlineScriptSnippet(raw: string): string {
  let snippet = (raw ?? '').trim();
  const wrapper = /^<script(?![^>]*\bsrc\s*=)[^>]*>([\s\S]*?)<\/script>$/i.exec(snippet);
  if (wrapper) snippet = wrapper[1].trim();
  return snippet;
}

/**
 * Extrait l'URL d'une balise `script src="…"` collée telle quelle dans le champ
 * « fichier externe » (l'utilisateur colle souvent la balise complète fournie
 * par le vendor). Sans balise src détectée, renvoie la valeur inchangée.
 */
export function extractScriptSrcFromTag(raw: string): string {
  const match = /<script[^>]*\bsrc\s*=\s*["']([^"']+)["'][^>]*>/i.exec(raw ?? '');
  return match ? match[1].trim() : (raw ?? '');
}

/**
 * Détection « ce script ressemble à un pixel Google/Meta » : signaux forts des
 * snippets officiels GA4/GTM/Google Ads et Meta Pixel. Purement indicatif côté
 * UI (bandeau « configurez-le plutôt dans Tracking » dans la modale Scripts) —
 * on n'empêche rien, on évite juste la double installation par mégarde.
 */
export const TRACKING_PIXEL_HINT_REGEX =
  /(googletagmanager\.com|google-analytics\.com|\bgtag\s*\(|\bGTM-[A-Z0-9]{4,}\b|connect\.facebook\.net|fbevents\.js|\bfbq\s*\()/i;

export const WebsiteConfigSchema = Type.Object({
  version: Type.String(),
  updateDate: Type.String(),
  theme: WebsiteStylesSchema,
  sharedComponents: Type.Optional(Type.Record(Type.String(), ComponentInstanceSchema)),
  pages: Type.Array(WebsitePageInstanceSchema),
  tracking: Type.Optional(TrackingConfigSchema),
  siteConfig: Type.Optional(SiteConfigSchema),
  // Onboarding v4, lot 7 : preset de style appliqué en dernier (`chic` | `doux` | `audacieux` |
  // `minimal`, cf. style-presets.ts). Absent = composition manuelle ou gabarit par défaut.
  template: Type.Optional(Type.String({ maxLength: 32 })),
});

// Manually defined to preserve discriminated PageComponent union (the schema uses a permissive flat object for AJV).
export interface WebsiteConfig {
  version: string;
  updateDate: string;
  theme: Static<typeof WebsiteStylesSchema>;
  sharedComponents?: Record<string, ComponentInstance>;
  pages: WebsitePageInstance[];
  tracking?: TrackingConfig;
  siteConfig?: SiteConfig;
  /** Preset de style appliqué en dernier (lot 7), `undefined` sans preset. */
  template?: string;
}

// ─── Site Languages (Google Translate target list for the header picker) ─
// Le français est la langue *source* du site (code '' = pas de traduction).
// Dans le builder, le pro choisit sur le composant header les langues de
// traduction à proposer ; cette liste pilote le sélecteur de langue, à la fois
// dans l'aperçu builder (BuilderLanguagePicker) et sur le site live (LanguagePicker
// → Google Traduction). Les libellés sont des endonymes (jamais traduits).
export interface SiteLanguage {
  /** Code Google Translate ('' = français, langue source du site). */
  code: string;
  /** Endonyme affiché dans le sélecteur (jamais traduit). */
  label: string;
  flag: string;
}

export const SITE_BASE_LANGUAGE: SiteLanguage = { code: '', label: 'Français', flag: '🇫🇷' };

/**
 * Code sentinelle du français (langue source) dans le multi-select du builder
 * (`language_selector`). Distinct de `SITE_BASE_LANGUAGE.code` (`''`) : le selector a
 * besoin d'une valeur non vide pour épingler/cocher le français. `resolveSiteLanguages`
 * l'ignore explicitement — le français est toujours réinjecté via `SITE_BASE_LANGUAGE`.
 */
export const SITE_BASE_LANGUAGE_SELECTOR_CODE = 'fr';

/** Langues de traduction sélectionnables par le pro (hors français, langue source). */
export const WEBSITE_TRANSLATION_LANGUAGES: readonly SiteLanguage[] = [
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'it', label: 'Italiano', flag: '🇮🇹' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { code: 'pt', label: 'Português', flag: '🇵🇹' },
  { code: 'nl', label: 'Nederlands', flag: '🇳🇱' },
  { code: 'ar', label: 'العربية', flag: '🇸🇦' },
  { code: 'zh-CN', label: '中文', flag: '🇨🇳' },
  { code: 'ru', label: 'Русский', flag: '🇷🇺' },
  { code: 'ja', label: '日本語', flag: '🇯🇵' },
];

/**
 * Construit la liste affichée par le sélecteur de langue à partir des codes choisis
 * par le pro : français (source) en tête, puis les langues de traduction configurées
 * dans l'ordre canonique de `WEBSITE_TRANSLATION_LANGUAGES`. Renvoie une liste vide si
 * aucune langue de traduction n'est configurée → le sélecteur reste masqué.
 */
export function resolveSiteLanguages(codes: string[] | undefined): SiteLanguage[] {
  // On exclut le français : code source `''` ET la sentinelle `'fr'` du selector. Il est
  // toujours réinjecté en tête via `SITE_BASE_LANGUAGE`, jamais comme « traduction ».
  const selected = new Set(
    (codes ?? []).filter(
      (c): c is string => typeof c === 'string' && c.length > 0 && c !== SITE_BASE_LANGUAGE_SELECTOR_CODE,
    ),
  );
  if (selected.size === 0) return [];
  const translations = WEBSITE_TRANSLATION_LANGUAGES.filter((l): boolean => selected.has(l.code));
  if (translations.length === 0) return [];
  return [SITE_BASE_LANGUAGE, ...translations];
}
